import type {
  MembershipInviteLifecycleStatus,
  MembershipInviteType,
} from './MembershipInviteLifecycle';

export interface MembershipInviteClaimRecord {
  inviteType: MembershipInviteType;
  status: string | null;
  acceptedUserId: string | null;
  seatReservationExpiresAt: string | null;
  seatReturnedAt: string | null;
}

export type MembershipInviteClaimReason =
  | 'FAMILY_PROFILE_CLAIM_ALLOWED'
  | 'SPONSORED_SEAT_CLAIM_ALLOWED'
  | 'ALREADY_CLAIMED_BY_CURRENT_USER'
  | 'ALREADY_CLAIMED_BY_ANOTHER_USER'
  | 'SPONSORED_RESERVATION_EXPIRED'
  | 'SPONSORED_SEAT_ALREADY_RETURNED'
  | 'INVITE_CANCELLED_OR_REJECTED'
  | 'INVALID_CLAIMANT';

export interface MembershipInviteClaimDecision {
  allowed: boolean;
  reason: MembershipInviteClaimReason;
  resultingStatus: MembershipInviteLifecycleStatus;
  markSeatClaimed: boolean;
  returnSeat: boolean;
}

const BLOCKED_STATUSES = new Set([
  'cancelled',
  'rejected',
  'failed',
]);

export function decideMembershipInviteClaim(
  record: MembershipInviteClaimRecord,
  claimantUserId: string,
  nowIso = new Date().toISOString(),
): MembershipInviteClaimDecision {
  if (!claimantUserId.trim()) {
    return Object.freeze({
      allowed: false,
      reason: 'INVALID_CLAIMANT',
      resultingStatus: 'FAILED',
      markSeatClaimed: false,
      returnSeat: false,
    });
  }

  if (
    record.acceptedUserId === claimantUserId ||
    record.status?.toLowerCase() === 'accepted'
  ) {
    return Object.freeze({
      allowed: record.acceptedUserId !== null,
      reason:
        record.acceptedUserId === claimantUserId
          ? 'ALREADY_CLAIMED_BY_CURRENT_USER'
          : 'ALREADY_CLAIMED_BY_ANOTHER_USER',
      resultingStatus: 'ACCEPTED',
      markSeatClaimed: false,
      returnSeat: false,
    });
  }

  if (
    record.acceptedUserId &&
    record.acceptedUserId !== claimantUserId
  ) {
    return Object.freeze({
      allowed: false,
      reason: 'ALREADY_CLAIMED_BY_ANOTHER_USER',
      resultingStatus: 'ACCEPTED',
      markSeatClaimed: false,
      returnSeat: false,
    });
  }

  if (
    record.status &&
    BLOCKED_STATUSES.has(record.status.toLowerCase())
  ) {
    return Object.freeze({
      allowed: false,
      reason: 'INVITE_CANCELLED_OR_REJECTED',
      resultingStatus:
        record.status.toLowerCase() === 'rejected'
          ? 'REJECTED'
          : record.status.toLowerCase() === 'cancelled'
            ? 'CANCELLED'
            : 'FAILED',
      markSeatClaimed: false,
      returnSeat:
        record.inviteType === 'SPONSORED_SEAT',
    });
  }

  if (record.inviteType === 'FAMILY_PROFILE') {
    return Object.freeze({
      allowed: true,
      reason: 'FAMILY_PROFILE_CLAIM_ALLOWED',
      resultingStatus: 'ACCEPTED',
      markSeatClaimed: false,
      returnSeat: false,
    });
  }

  if (record.seatReturnedAt) {
    return Object.freeze({
      allowed: false,
      reason: 'SPONSORED_SEAT_ALREADY_RETURNED',
      resultingStatus: 'RETURNED',
      markSeatClaimed: false,
      returnSeat: false,
    });
  }

  const reservationExpiresAt =
    record.seatReservationExpiresAt
      ? new Date(record.seatReservationExpiresAt).getTime()
      : Number.NaN;

  const now = new Date(nowIso).getTime();

  if (
    !Number.isFinite(reservationExpiresAt) ||
    reservationExpiresAt <= now
  ) {
    return Object.freeze({
      allowed: false,
      reason: 'SPONSORED_RESERVATION_EXPIRED',
      resultingStatus: 'EXPIRED',
      markSeatClaimed: false,
      returnSeat: true,
    });
  }

  return Object.freeze({
    allowed: true,
    reason: 'SPONSORED_SEAT_CLAIM_ALLOWED',
    resultingStatus: 'ACCEPTED',
    markSeatClaimed: true,
    returnSeat: false,
  });
}

export function isInviteLifecycleSchemaError(
  message: string,
): boolean {
  const value = message.toLowerCase();

  return (
    value.includes('schema cache') ||
    value.includes('invite_type') ||
    value.includes('seat_reservation_expires_at') ||
    value.includes('seat_claimed_at') ||
    value.includes('seat_returned_at') ||
    value.includes('membership_owner_user_id')
  );
}

export const MEMBERSHIP_INVITE_CLAIM_RUNTIME_READY = true;
